import { request } from '@codametrix/client-api';
import { isCaseDetailTwoPhaseEnabled } from '@codametrix/client-runtime';

/**
 * Case Accelerator — two-phase case-detail load (cache-first Phase 1, then authoritative
 * Phase 2 reconcile) gated by `CASE_ACCELERATOR_UI_ENABLED`. Gateway protocol: `Accept-Cache` /
 * `X-Cache` (CDMX-16156).
 */

/** Gateway cache disposition (CDMX-16156). Parsed from response header `X-Cache`. */
export type CaseDetailCacheOutcome = 'HIT' | 'MISS' | 'BYPASS';

/**
 * Result of one case-detail HTTP round-trip when cache metadata is preserved.
 * Used by {@link requestJSONWithCacheMeta} and as the building block for phased fetches.
 */
export type CaseDetailCacheMeta = {
  /** Parsed JSON body (case detail on success; `null` for 204 No Content). */
  data: unknown;
  /**
   * How the gateway handled cache for this request.
   * `undefined` when the header is absent (pre-gateway or flag-off single fetch).
   */
  xCache: CaseDetailCacheOutcome | undefined;
};

/**
 * Outcome of {@link orchestrateTwoPhaseCaseDetailFetch} for Cases (`getDetail`) and
 * coding bench (`loadFromURLMatch`).
 *
 * Flow when two-phase is enabled (`CASE_ACCELERATOR_UI_ENABLED`):
 * 1. Phase 1 — `Accept-Cache: true`; render immediately (via `onPhase1Complete` on HIT).
 * 2. Phase 2 — only if Phase 1 returned `xCache: 'HIT'`; `Accept-Cache: false`
 *    (authoritative Automate fetch). For IPC / Bedside Pro, Phase 2 also appends
 *    `refresh=true` so XCaliber participates in the reconcile fetch.
 * 3. Compare active CaseRun `last_entity_update` between phases to detect drift.
 */
/** One changed active-CaseRun path with Phase 1 (left) and Phase 2 (right) values. */
export type CaseDetailChangedEntry = {
  path: string;
  left: unknown;
  right: unknown;
};

export type TwoPhaseCaseDetailResult<T> = {
  /**
   * Payload callers should render.
   * On drift after a cache HIT, this is Phase 2 (auto-applied). On freshness warning, Phase 1.
   */
  data: T;
  /** Phase 1 cache outcome when two-phase ran; omitted on flag-off single fetch. */
  xCache?: CaseDetailCacheOutcome;
  /**
   * When true, skip heavy post-load supporting data (code descriptions, billing group, process
   * instance, related cases, etc.). Set when Phase 2 fails after a HIT (freshness warning).
   */
  deferSupportingData: boolean;
  /**
   * Phase 2 differed from Phase 1 and was auto-applied as `data`.
   * `reconciliationDiff` is retained for a follow-up comparison modal (stacked PR).
   */
  autoAppliedReconciliation?: boolean;
  /** Meaningful path/value pairs when {@link autoAppliedReconciliation} is true. */
  reconciliationDiff?: CaseDetailChangedEntry[];
  /**
   * Phase 2 failed after a cache HIT. UI shows non-blocking "Unable to verify freshness";
   * `data` remains the Phase 1 cached view.
   */
  freshnessWarning?: boolean;
};

/** True when a fetch was cancelled because a newer case-detail load took the abort key. */
export function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

export function parseXCacheHeader(response: Response): CaseDetailCacheOutcome | undefined {
  const raw = response.headers.get('X-Cache') ?? response.headers.get('x-cache');
  if (!raw) {
    return undefined;
  }
  const normalized = raw.trim().toUpperCase();
  if (normalized === 'HIT' || normalized === 'MISS' || normalized === 'BYPASS') {
    return normalized;
  }
  return undefined;
}

function headersInitToRecord(headers?: HeadersInit): Record<string, string> {
  if (!headers) {
    return {};
  }
  if (headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return { ...headers };
}

function mergeAcceptCacheHeader(
  init: RequestInit,
  acceptCache: boolean | undefined
): RequestInit {
  if (acceptCache === undefined) {
    return init;
  }
  // Plain object, not Headers: `@codametrix/client-api` spreads `init.headers`
  // into a record, which drops Headers instances (no enumerable own properties).
  return {
    ...init,
    headers: {
      ...headersInitToRecord(init.headers),
      'Accept-Cache': acceptCache ? 'true' : 'false'
    }
  };
}

/**
 * JSON fetch that preserves X-Cache for the gateway case-detail cache protocol.
 * Does not append refresh=true — that param is for XCaliber refresh only.
 */
export async function requestJSONWithCacheMeta<T>(
  input: RequestInfo,
  init: RequestInit = {},
  acceptCache?: boolean
): Promise<{ data: T; xCache: CaseDetailCacheOutcome | undefined }> {
  const response = await request(input, mergeAcceptCacheHeader(init, acceptCache));
  const xCache = parseXCacheHeader(response);

  if (response.status === 204) {
    return { data: null as T, xCache };
  }

  const data = (await response.json()) as T;
  return { data, xCache };
}

/**
 * Diagnostic and run-selection context for {@link caseDetailMeaningfulDataDiffers}.
 * `processId` selects the CaseRun the caller is actually displaying; without it the primary run
 * is used, matching `getCaseRun`.
 */
export type CaseDetailDiffContext = {
  processId?: string | number;
  /** Whether Phase 2 appended `refresh=true` (IPC / Bedside Pro), which pulls fresh XCaliber data. */
  phase2UsedRefresh?: boolean;
};

type CaseDetailDiffInput = string | number | CaseDetailDiffContext;

function normalizeDiffContext(
  context?: CaseDetailDiffInput
): CaseDetailDiffContext {
  if (context === undefined || context === null) {
    return {};
  }
  return typeof context === 'object' ? context : { processId: context };
}

/**
 * Meaningful active-CaseRun diff between cache (Phase 1) and fresh (Phase 2) payloads.
 * Paths with data-bearing dynamic keys are redacted for display/storage.
 * Side effect: when changes exist, logs a temporary diagnostic `console.group`.
 */
export function caseDetailDiff(
  phase1: CMxAPI.Case,
  phase2: CMxAPI.Case,
  context?: CaseDetailDiffInput
): { differs: boolean; changes: CaseDetailChangedEntry[] } {
  const { processId, phase2UsedRefresh } = normalizeDiffContext(context);
  const changes = caseDetailChangedEntries(phase1, phase2, processId);
  const displayChanges = changes.map(change => ({
    ...change,
    path: sanitizeDebugPath(change.path)
  }));
  if (changes.length > 0) {
    // Temporary: group left (Phase 1 / cache) vs right (Phase 2 / authoritative) for diagnosis.
    const annotations = [
      describeCacheStaleness(phase1, phase2, processId),
      phase2UsedRefresh === undefined
        ? undefined
        : `phase 2 refresh=${phase2UsedRefresh}`
    ].filter(annotation => annotation !== undefined);
    console.group(
      `[case-accelerator] Meaningful reconciliation differences (${
        changes.length
      }) — ${annotations.join('; ')}`
    );
    displayChanges.forEach(({ path, left, right }) => {
      console.debug(path, { left, right });
    });
    console.groupEnd();
  }
  return { differs: changes.length > 0, changes: displayChanges };
}

export function caseDetailMeaningfulDataDiffers(
  phase1: CMxAPI.Case,
  phase2: CMxAPI.Case,
  context?: CaseDetailDiffInput
): boolean {
  return caseDetailDiff(phase1, phase2, context).differs;
}

function formatStalenessDuration(milliseconds: number): string {
  const totalMinutes = Math.round(milliseconds / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [
    ...(days > 0 ? [`${days}d`] : []),
    ...(days > 0 || hours > 0 ? [`${hours}h`] : []),
    `${minutes}m`
  ];
  return parts.join(' ');
}

/**
 * `last_entity_update` is excluded from drift detection, but it is the only direct measure of how
 * far behind the cached payload is — so it is surfaced alongside the diff instead.
 */
function describeCacheStaleness(
  phase1: CMxAPI.Case,
  phase2: CMxAPI.Case,
  processId?: string | number
): string {
  const cached = readLastEntityUpdate(phase1, processId);
  const authoritative = readLastEntityUpdate(phase2, processId);
  const summary = `cache last_entity_update ${cached ?? 'absent'}, authoritative ${authoritative ??
    'absent'}`;

  const cachedInstant = comparableDateTime(cached)?.instant;
  const authoritativeInstant = comparableDateTime(authoritative)?.instant;
  if (cachedInstant === undefined || authoritativeInstant === undefined) {
    return summary;
  }

  const drift = authoritativeInstant - cachedInstant;
  if (drift <= 0) {
    return `${summary} (cache not behind)`;
  }
  return `${summary} (cache ${formatStalenessDuration(drift)} behind)`;
}

/** @deprecated Use {@link caseDetailMeaningfulDataDiffers}. */
export function caseDetailLastEntityUpdateDiffers(
  phase1: CMxAPI.Case,
  phase2: CMxAPI.Case,
  processId?: string | number
): boolean {
  return caseDetailMeaningfulDataDiffers(phase1, phase2, processId);
}

const ignoredCaseRunPathPatterns = [
  /^last_entity_update$/,
  /^discharge_datetime$/,
  /(^|\.)admission_datetime$/,
  /(^|\.)medicare_consult_rules$/,
  /(^|\.)medicare_time_rules$/,
  /^invoices\.\d+\.transactions\.\d+\.guid$/,
  /^facts(\.|$)/
];

function sanitizeDebugPath(path: string): string {
  const segments = path.split('.');
  const firstDynamicSegment = segments.findIndex(
    segment => !/^(?:[A-Za-z_]\w*|\d+)$/.test(segment)
  );
  if (firstDynamicSegment === -1) {
    return path;
  }
  return [...segments.slice(0, firstDynamicSegment), '<dynamic-key>'].join('.');
}

const timeZoneOffsets: Record<string, number> = {
  UTC: 0,
  EST: -5,
  EDT: -4,
  CST: -6,
  CDT: -5,
  MST: -7,
  MDT: -6,
  PST: -8,
  PDT: -7
};

type ComparableDateTime = {
  instant: number | undefined;
  wallTime: string;
};

function comparableDateTime(value: unknown): ComparableDateTime | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const dateTimePattern =
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2}) ([A-Z]{3})$/;
  const match = dateTimePattern.exec(value);
  if (!match) {
    return undefined;
  }

  const [, year, month, day, hour, minute, second, zone] = match;
  const offset = timeZoneOffsets[zone];
  const wallTime = `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  const instant =
    offset === undefined
      ? undefined
      : Date.UTC(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour) - offset,
          Number(minute),
          Number(second)
        );

  return { instant, wallTime };
}

function dateTimesAreEquivalent(left: unknown, right: unknown): boolean {
  const leftDateTime = comparableDateTime(left);
  const rightDateTime = comparableDateTime(right);
  if (!leftDateTime || !rightDateTime) {
    return false;
  }

  return (
    leftDateTime.wallTime === rightDateTime.wallTime ||
    (leftDateTime.instant !== undefined &&
      leftDateTime.instant === rightDateTime.instant)
  );
}

function isIgnoredCaseRunPath(path: string): boolean {
  return ignoredCaseRunPathPatterns.some(pattern => pattern.test(path));
}

function collectChangedPaths(
  left: unknown,
  right: unknown,
  path: string,
  changedPaths: CaseDetailChangedEntry[]
): void {
  if (
    isIgnoredCaseRunPath(path) ||
    left === right ||
    dateTimesAreEquivalent(left, right)
  ) {
    return;
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) {
      changedPaths.push({
        path: `${path}.length`,
        left: left.length,
        right: right.length
      });
    }
    const sharedLength = Math.min(left.length, right.length);
    for (let index = 0; index < sharedLength; index += 1) {
      collectChangedPaths(
        left[index],
        right[index],
        path ? `${path}.${index}` : `${index}`,
        changedPaths
      );
    }
    return;
  }

  if (
    left !== null &&
    right !== null &&
    typeof left === 'object' &&
    typeof right === 'object'
  ) {
    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const keys = Array.from(
      new Set([...Object.keys(leftRecord), ...Object.keys(rightRecord)])
    ).sort((leftKey, rightKey) => leftKey.localeCompare(rightKey));
    keys.forEach(key => {
      collectChangedPaths(
        leftRecord[key],
        rightRecord[key],
        path ? `${path}.${key}` : key,
        changedPaths
      );
    });
    return;
  }

  changedPaths.push({
    path: path || 'activeCaseRun',
    left,
    right
  });
}

function readActiveCaseRun(
  caseDetail: CMxAPI.Case,
  processId?: string | number
): CMxAPI.CaseRun | undefined {
  const runs = caseDetail?.caseRuns ?? [];
  if (!runs.length) {
    return undefined;
  }

  if (processId !== undefined && processId !== null && `${processId}` !== '') {
    const match = runs.find(
      run =>
        `${run.process_instance_id}` === `${processId}` ||
        `${run.process_instance_guid}` === `${processId}`
    );
    if (match) {
      return match;
    }
  }

  // A supplied process id that matches nothing must still fall through to the primary run rather
  // than runs[0]; run ordering is not guaranteed to be stable between cache and origin.
  return runs.find(run => run.primary_process_indicator === true) ?? runs[0];
}

function identityPart(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}`;
  }
  if (typeof value === 'string' && value !== '') {
    return value;
  }
  return undefined;
}

/**
 * Stable identity for a CaseRun, used to compare like with like across payloads.
 * `undefined` when neither process identifier is present (payloads that predate them).
 */
function readCaseRunIdentity(run: CMxAPI.CaseRun | undefined): string | undefined {
  if (!run) {
    return undefined;
  }
  const id = identityPart(run.process_instance_id);
  const guid = identityPart(
    (run as { process_instance_guid?: unknown }).process_instance_guid
  );
  if (id === undefined && guid === undefined) {
    return undefined;
  }
  return [id ?? '-', guid ?? '-'].join('/');
}

function isSameCaseRun(
  left: CMxAPI.CaseRun,
  right: CMxAPI.CaseRun
): boolean {
  const pairs: Array<[unknown, unknown]> = [
    [left.process_instance_id, right.process_instance_id],
    [
      (left as { process_instance_guid?: unknown }).process_instance_guid,
      (right as { process_instance_guid?: unknown }).process_instance_guid
    ]
  ];
  return pairs.some(([leftValue, rightValue]) => {
    const leftPart = identityPart(leftValue);
    const rightPart = identityPart(rightValue);
    return leftPart !== undefined && leftPart === rightPart;
  });
}

function readCaseRunIdentities(caseDetail: CMxAPI.Case): string[] {
  return (caseDetail?.caseRuns ?? [])
    .map(readCaseRunIdentity)
    .filter((identity): identity is string => identity !== undefined)
    .sort((left, right) => left.localeCompare(right));
}

/**
 * Pairs the displayed cached run with the same run in the authoritative payload by identity, so an
 * unstable `caseRuns` ordering cannot produce a field-by-field diff between two unrelated runs.
 * `aligned` is false when the cached run is absent from the authoritative payload.
 */
function readComparableCaseRuns(
  phase1: CMxAPI.Case,
  phase2: CMxAPI.Case,
  processId?: string | number
): {
  cached: CMxAPI.CaseRun | undefined;
  authoritative: CMxAPI.CaseRun | undefined;
  aligned: boolean;
} {
  const cached = readActiveCaseRun(phase1, processId);
  const fallback = readActiveCaseRun(phase2, processId);
  if (!cached || readCaseRunIdentity(cached) === undefined) {
    return { cached, authoritative: fallback, aligned: true };
  }

  const matched = (phase2?.caseRuns ?? []).find(run =>
    isSameCaseRun(cached, run)
  );
  return matched
    ? { cached, authoritative: matched, aligned: true }
    : { cached, authoritative: fallback, aligned: false };
}

/**
 * Returns meaningful active-CaseRun path/value pairs that changed between cache and
 * authoritative payloads. Reconciliation metadata, `discharge_datetime`, generated
 * transaction GUIDs, and the rule-engine `facts` bag (not shown in case-detail UI) are
 * ignored, and equivalent date-times formatted in UTC versus a US timezone are treated as
 * equal.
 *
 * A changed set of CaseRuns, or a displayed run missing from the authoritative payload, is reported
 * as a single identity difference rather than as drift on every field.
 */
function caseDetailChangedEntries(
  phase1: CMxAPI.Case,
  phase2: CMxAPI.Case,
  processId?: string | number
): CaseDetailChangedEntry[] {
  const changedPaths: CaseDetailChangedEntry[] = [];

  const cachedIdentities = readCaseRunIdentities(phase1);
  const authoritativeIdentities = readCaseRunIdentities(phase2);
  if (
    cachedIdentities.length > 0 &&
    authoritativeIdentities.length > 0 &&
    cachedIdentities.join(',') !== authoritativeIdentities.join(',')
  ) {
    changedPaths.push({
      path: 'caseRuns.identities',
      left: cachedIdentities,
      right: authoritativeIdentities
    });
  }

  const { cached, authoritative, aligned } = readComparableCaseRuns(
    phase1,
    phase2,
    processId
  );

  if (!aligned) {
    changedPaths.push({
      path: 'activeCaseRun.identity',
      left: readCaseRunIdentity(cached),
      right: readCaseRunIdentity(authoritative) ?? null
    });
    return changedPaths;
  }

  collectChangedPaths(cached, authoritative, '', changedPaths);
  return changedPaths;
}

/**
 * Returns meaningful active-CaseRun paths that changed between cache and authoritative payloads.
 * Reconciliation metadata, `discharge_datetime`, generated transaction GUIDs, and the `facts` bag are ignored, and
 * equivalent date-times formatted in UTC versus a US timezone are treated as equal.
 */
export function caseDetailChangedPaths(
  phase1: CMxAPI.Case,
  phase2: CMxAPI.Case,
  processId?: string | number
): string[] {
  return caseDetailChangedEntries(phase1, phase2, processId).map(
    entry => entry.path
  );
}

export function readLastEntityUpdate(
  caseDetail: CMxAPI.Case,
  processId?: string | number
): string | null {
  const activeRun = readActiveCaseRun(caseDetail, processId);
  if (!activeRun) {
    return null;
  }

  const value = (activeRun as { last_entity_update?: string | null }).last_entity_update;
  return value ?? null;
}

/**
 * Appends `refresh=true` for Phase 2 on IPC / Bedside Pro so the reconcile fetch
 * also pulls fresh XCaliber data. Idempotent if the param is already present.
 */
export function appendCaseDetailRefreshParam(url: string): string {
  // The base (http://localhost) is needed because case-detail URLs are relative paths (/api/cases/...); 
  // URL requires an absolute URL to parse them. We return only pathname + search, so the base never appears in the result.
  const parsed = new URL(url, 'http://localhost');
  if (parsed.searchParams.get('refresh') === 'true') {
    return url;
  }
  parsed.searchParams.set('refresh', 'true');
  return `${parsed.pathname}${parsed.search}`;
}

/** One case-detail fetch returning body + parsed `X-Cache` (typically wraps {@link requestJSONWithCacheMeta}). */
export type PhaseFetch<T> = () => Promise<{
  data: T;
  xCache: CaseDetailCacheOutcome | undefined;
}>;

/**
 * Inputs for {@link orchestrateTwoPhaseCaseDetailFetch}.
 * Callers inject transport-specific fetchers (`getDetail` via dashboard `net.ts`,
 * bench via `@codametrix/client-api`) while sharing the same phase logic.
 */
export type OrchestrateTwoPhaseCaseDetailFetchOptions<T> = {
  /**
   * Override runtime flag check. Defaults to `isCaseDetailTwoPhaseEnabled()`.
   * When false, only `single` (or `phase1` as fallback) runs — no cache headers required.
   */
  enabled?: boolean;
  /**
   * Flag-off fetch path: ordinary single request without `Accept-Cache` headers.
   * Prefer this over `phase1` when two-phase is disabled so legacy behavior is preserved.
   */
  single?: PhaseFetch<T>;
  /** Phase 1 — `Accept-Cache: true`. Cache-first shell; may return `xCache: 'HIT'`. */
  phase1: PhaseFetch<T>;
  /**
   * Phase 2 — `Accept-Cache: false`. Authoritative reconcile; runs only after Phase 1 `HIT`.
   * For IPC / Bedside Pro, append {@link appendCaseDetailRefreshParam} to the URL.
   */
  phase2: PhaseFetch<T>;
  /**
   * Recovery when Phase 1 throws: one authoritative fetch with `Accept-Cache: false`.
   * Failure here propagates to the caller's existing error handling.
   */
  fallback: PhaseFetch<T>;
  /**
   * Hook to render Phase 1 before Phase 2 starts. Callers dispatch fulfilled actions /
   * bench load here on `xCache: 'HIT'` so the UI paints cached data immediately.
   */
  onPhase1Complete?: (
    data: T,
    xCache: CaseDetailCacheOutcome | undefined
  ) => void | Promise<void>;
  /**
   * Drift detector between Phase 1 and Phase 2.
   * Return `true`/`false`, or `{ differs, changes }` so callers can retain the diff for a
   * comparison modal after auto-apply.
   */
  differs: (
    phase1: T,
    phase2: T
  ) =>
    | boolean
    | { differs: boolean; changes?: CaseDetailChangedEntry[] }
    | Promise<
        | boolean
        | { differs: boolean; changes?: CaseDetailChangedEntry[] }
      >;
};

function normalizeDiffersResult(
  result:
    | boolean
    | { differs: boolean; changes?: CaseDetailChangedEntry[] }
): { differs: boolean; changes: CaseDetailChangedEntry[] } {
  if (typeof result === 'boolean') {
    return { differs: result, changes: [] };
  }
  return { differs: result.differs, changes: result.changes ?? [] };
}

/**
 * Shared two-phase orchestration for case-detail URLs.
 * Phase 2 runs only after Phase 1 `X-Cache: HIT`; uses `Accept-Cache: false`.
 * IPC / Bedside Pro Phase 2 URLs should include `refresh=true` (see {@link appendCaseDetailRefreshParam}).
 */
export async function orchestrateTwoPhaseCaseDetailFetch<T>(
  options: OrchestrateTwoPhaseCaseDetailFetchOptions<T>
): Promise<TwoPhaseCaseDetailResult<T>> {
  const enabled = options.enabled ?? isCaseDetailTwoPhaseEnabled();

  if (!enabled) {
    const single = await (options.single ?? options.phase1)();
    return { data: single.data, xCache: single.xCache, deferSupportingData: false };
  }

  try {
    const phase1 = await options.phase1();
    await options.onPhase1Complete?.(phase1.data, phase1.xCache);

    if (phase1.xCache !== 'HIT') {
      return {
        data: phase1.data,
        xCache: phase1.xCache,
        deferSupportingData: false
      };
    }

    try {
      const phase2 = await options.phase2();
      const diff = normalizeDiffersResult(
        await options.differs(phase1.data, phase2.data)
      );
      if (diff.differs) {
        // Auto-apply Phase 2. Phase 1 already painted via onPhase1Complete; callers replace
        // with `data`. Retain reconciliationDiff for a stacked comparison-modal PR.
        return {
          data: phase2.data,
          xCache: 'HIT',
          deferSupportingData: false,
          autoAppliedReconciliation: true,
          reconciliationDiff: diff.changes
        };
      }
      return {
        data: phase2.data,
        xCache: 'HIT',
        deferSupportingData: false
      };
    } catch (error) {
      // A newer getDetail / loadFromURLMatch calls getSignal(DETAIL_RECONCILE), which
      // aborts this Phase 2. That is supersession, not a freshness failure — treating
      // it as a warning is what flashes "Unable to get the latest information" on the
      // first related-case open (mount + URL settle both start a load).
      if (isAbortError(error)) {
        throw error;
      }
      return {
        data: phase1.data,
        xCache: 'HIT',
        deferSupportingData: true,
        freshnessWarning: true
      };
    }
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    const fallback = await options.fallback();
    return {
      data: fallback.data,
      xCache: fallback.xCache,
      deferSupportingData: false
    };
  }
}
